/**
 * The firm's mark, name and tagline, in one definition (2026-09-30).
 *
 * THE BUG THIS FIXES: the Settings "Preview" was a hand-copy of the top bar
 * that had drifted from it. It drew the fallback mark WITHOUT the monogram
 * inside — an empty gold circle — and styled the name with whatever `.brand-name`
 * happened to inherit, which on desktop was nothing at all. So the pane Mick
 * looked at showed a faint ring and some plain text, and read as blank.
 *
 * A preview that is a copy of the thing it previews is wrong the first time
 * either one is edited. So the top bar, the preview and the client's share
 * page now render the SAME markup from here, and differ only by a variant
 * class that sets sizes.
 *
 * Pure: takes URLs, returns a string. Nothing here fetches or touches storage.
 */

import { esc } from '@/lib/dom';
import { companyName, tagline, brandInitials, type Branding } from '@/lib/branding';
import { brandFontStack, brandColor } from '@/lib/brand-type';

export type BrandVariant =
  /** The app's top bar, and the Settings preview of it. */
  | 'nav'
  /** The client's share page header. */
  | 'portal';

export interface BrandLockupOptions {
  branding: Branding | null;
  /** The firm's own name, when the caller knows it and the branding doesn't. */
  firmName?: string | null;
  variant: BrandVariant;
  /** Resolved URLs — this module does not know about storage. */
  logoUrl?: string | null;
  photoUrl?: string | null;
  /** Appended to the tagline, after a separator. The portal says "Client view". */
  tagSuffix?: string | null;
}

/**
 * The monogram mark, drawn when a firm has no logo.
 *
 * The letters are the part the Settings copy lost. Without them the circle is
 * decoration; with them it is a placeholder that says "your logo goes here".
 */
export function monogramMark(initials: string, className = 'brand-mark'): string {
  return `<svg class="${esc(className)}" viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="32" r="30" stroke="#B89A6A" stroke-width="1.5" fill="none"/>
      <text x="32" y="41" text-anchor="middle" font-family="Cormorant Garamond, serif"
            font-size="24" fill="#1A1714" letter-spacing="2">${esc(initials)}</text>
    </svg>`;
}

/** Mark, name and tagline — the same lockup everywhere it appears. */
export function brandLockup(o: BrandLockupOptions): string {
  const { branding: b, variant } = o;
  const name = companyName(b, o.firmName);
  // De-duplicated: before a profile loads, `branding` is null and tagline()
  // answers with the product's own "Project Hub" — the same words the shell
  // passes as the suffix. Joined blindly that reads "Project Hub · Project
  // Hub" on every page load, for as long as the profile takes to arrive.
  const parts = [tagline(b), o.tagSuffix]
    .map((x) => (x ?? '').trim())
    .filter((x) => x !== '');
  const tag = [...new Set(parts)].join(' · ');
  const nameStyle = `font-family:${brandFontStack(b?.brand_font)};color:${brandColor(b?.brand_color)}`;

  // The photo leads: it is the person, and the logo is the firm. On a 60px bar
  // this is the only order that leaves the mark beside the name it belongs to.
  const photo = o.photoUrl
    ? `<img class="brand-photo" src="${esc(o.photoUrl)}" alt="" />`
    : '';
  const mark = o.logoUrl
    ? `<img class="brand-mark" src="${esc(o.logoUrl)}" alt="" />`
    : monogramMark(brandInitials(b, o.firmName));

  return `<span class="brand-lockup brand-lockup-${esc(variant)}">
      ${photo}${mark}
      <span class="brand-words">
        <span class="brand-name" data-brand-name style="${esc(nameStyle)}">${esc(name)}</span>
        ${tag ? `<span class="brand-tag" data-brand-tag>${esc(tag)}</span>` : ''}
      </span>
    </span>`;
}

/**
 * What to upload, in the words the Settings page uses.
 *
 * Kept next to the markup because these numbers are the RENDERED box doubled
 * for a retina screen — change a size in the CSS and this has to move with it.
 * `tests/lib/brand-lockup.test.ts` checks they stay in step with the stylesheet.
 */
export const BRAND_IMAGE_GUIDANCE = {
  /** .brand-lockup-nav .brand-mark is 38px. */
  logo: {
    displayedPx: 38,
    text: 'Square, at least 200 × 200 px. PNG with a transparent background looks best — '
      + 'it sits on white in the app and on your client’s page. A wide logo still works: '
      + 'it is fitted inside a square without being cropped.',
  },
  /** .brand-lockup-nav .brand-photo is 34px, and cropped to a circle. */
  photo: {
    displayedPx: 34,
    text: 'Square, at least 200 × 200 px, with your face centered — it is cropped to a circle, '
      + 'so anything in the corners is trimmed. It shows small (about 34 px), so a close portrait '
      + 'reads better than a full-length shot.',
  },
} as const;
